import type { EvaluationMethodConfig } from './sweetSpotEvaluationEngine';
import { makeCustomParameterKey } from './sweetSpotCurveCatalog';
import type {
  ParameterDirection, ParameterGroup, SweetSpotParameter, SweetSpotParameterConfig,
} from './types';

export type CustomParameterInput = {
  label: string;
  unit?: string;
  sourceColumnKey?: string;
  direction: Exclude<ParameterDirection, 'target_range'>;
};

function normalizeInput(input: CustomParameterInput) {
  const label = input.label.trim();
  if (!label) throw new Error('请输入评价参数名称。');
  return {
    label,
    unit: input.unit?.trim() || '无量纲',
    sourceColumnKey: input.sourceColumnKey?.trim().toUpperCase() || undefined,
    direction: input.direction,
  };
}

function assertUniqueLabel(
  parameters: SweetSpotParameterConfig[ParameterGroup],
  label: string,
  excludedId?: string,
) {
  const normalized = label.toLocaleLowerCase();
  if (parameters.some((parameter) => parameter.id !== excludedId
    && parameter.label.trim().toLocaleLowerCase() === normalized)) {
    throw new Error(`“${label}”已存在，请使用不同的参数名称。`);
  }
}

function nextCustomKey(config: SweetSpotParameterConfig, seed: string): `custom_${string}` {
  const base = makeCustomParameterKey(seed);
  const used = new Set([...config.rq, ...config.cq].map((parameter) => parameter.key));
  if (!used.has(base)) return base;
  let suffix = 2;
  while (used.has(`${base}_${suffix}` as `custom_${string}`)) suffix += 1;
  return `${base}_${suffix}` as `custom_${string}`;
}

function defaultWeight(parameters: SweetSpotParameter[]): number {
  const enabled = parameters.filter((parameter) => parameter.enabled);
  if (!enabled.length) return 1;
  const total = enabled.reduce((sum, parameter) => sum + (
    Number.isFinite(parameter.weight) && parameter.weight >= 0 ? parameter.weight : 0
  ), 0);
  return total > 0 ? total / enabled.length : 1 / (enabled.length + 1);
}

export function addCustomParameter(
  config: EvaluationMethodConfig,
  group: ParameterGroup,
  input: CustomParameterInput,
): EvaluationMethodConfig {
  const normalized = normalizeInput(input);
  assertUniqueLabel(config.parameters[group], normalized.label);
  const key = nextCustomKey(config.parameters, normalized.sourceColumnKey || normalized.label);
  const parameter: SweetSpotParameter = {
    id: `${group}.${key}`,
    key,
    group,
    label: normalized.label,
    unit: normalized.unit,
    sourceColumnKey: normalized.sourceColumnKey,
    direction: normalized.direction,
    enabled: true,
    weight: defaultWeight(config.parameters[group]),
    custom: true,
  };
  return {
    ...config,
    ahp: undefined,
    reviewState: undefined,
    parameters: {
      ...config.parameters,
      [group]: [...config.parameters[group], parameter],
    },
  };
}

export function updateCustomParameter(
  config: EvaluationMethodConfig,
  group: ParameterGroup,
  parameterId: string,
  input: CustomParameterInput,
): EvaluationMethodConfig {
  const existing = config.parameters[group].find((parameter) => parameter.id === parameterId);
  if (!existing?.custom) throw new Error('只能编辑用户新增的评价参数。');
  const normalized = normalizeInput(input);
  assertUniqueLabel(config.parameters[group], normalized.label, parameterId);
  return {
    ...config,
    ahp: undefined,
    reviewState: undefined,
    parameters: {
      ...config.parameters,
      [group]: config.parameters[group].map((parameter) => parameter.id === parameterId ? {
        ...parameter,
        ...normalized,
      } : parameter),
    },
  };
}

export function removeCustomParameter(
  config: EvaluationMethodConfig,
  group: ParameterGroup,
  parameterId: string,
): EvaluationMethodConfig {
  const existing = config.parameters[group].find((parameter) => parameter.id === parameterId);
  if (!existing?.custom) throw new Error('只能移除用户新增的评价参数。');
  return {
    ...config,
    ahp: undefined,
    reviewState: undefined,
    parameters: {
      ...config.parameters,
      [group]: config.parameters[group].filter((parameter) => parameter.id !== parameterId),
    },
  };
}
